import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import ConnectionFailedError from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.component';
import ConnectionEscalated from 'src/components/WhatsAppConnect/ConnectionEscalated/ConnectionEscalated.component';
import { useFocusTrap } from 'src/components/AlertCenter/AlertPanel.hook';
import { useMediaQuery } from 'src/hooks/useMediaQuery.hook';
import { useProfilingProgressContext } from 'src/providers/ProfilingProgressProvider/ProfilingProgress.context';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import { useWhatsAppConnectionWizard } from './WhatsAppConnectionWizard.hook';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_CONNECTION_FLOW,
  WIZARD_AFTER_CONNECTING_ROWS,
  WIZARD_DESKTOP_MEDIA_QUERY,
  WIZARD_GENERAL_PHONE_REQUIREMENT_ROW,
  WIZARD_ICON_CLASS_NAME,
  WIZARD_PATH_CARDS,
  WIZARD_PATH_REQUIREMENT_ROWS,
  WIZARD_REQUIREMENT_ROWS,
  WIZARD_STEP_BY_PHASE,
  WIZARD_STEP_COUNT,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import type {
  tWhatsAppConnectionWizardHook,
  tWizardAdvisorLocation,
  tWizardAsideGroup,
  tWizardAsideRow,
  tWizardConnectionVideoLocation,
  tWizardPathAnswerPoint,
  tWizardPathCard,
} from './WhatsAppConnectionWizard.types';
import {
  canConnectWithoutGuide,
  findWizardErrorCatalogEntry,
  isWindowFullScreen,
  millisecondsUntilActivationEscalation,
  resolveActivationFailure,
  resolveExchangeError,
  resolveFailureFooter,
  resolveHistorySync,
} from './WhatsAppConnectionWizard.functions';
import WhatsAppConnectionWizardWaiting from './WhatsAppConnectionWizardWaiting.component';
import WhatsAppConnectionWizardReportedError from './WhatsAppConnectionWizardReportedError.component';
import WhatsAppConnectionWizardClosedBeforeFinishing from './WhatsAppConnectionWizardClosedBeforeFinishing.component';
import WhatsAppConnectionWizardErrorAside from './WhatsAppConnectionWizardErrorAside.component';
import WhatsAppConnectionWizardMetaGuide from './WhatsAppConnectionWizardMetaGuide.component';
import WhatsAppConnectionWizardActivating from './WhatsAppConnectionWizardActivating.component';
import WhatsAppConnectionWizardActivationPending from './WhatsAppConnectionWizardActivationPending.component';
import WhatsAppConnectionWizardActivationFailed from './WhatsAppConnectionWizardActivationFailed.component';
import WhatsAppConnectionWizardExchangeError from './WhatsAppConnectionWizardExchangeError.component';
import WhatsAppConnectionWizardConnectionSummary from './WhatsAppConnectionWizardConnectionSummary.component';
import WhatsAppConnectionWizardConnected, {
  WhatsAppConnectionWizardGoToFunnelButton,
} from './WhatsAppConnectionWizardConnected.component';
import WhatsAppConnectionWizardTeamInvite from './WhatsAppConnectionWizardTeamInvite.component';
import WhatsAppConnectionWizardPathDrawing from './WhatsAppConnectionWizardPathDrawing.component';
import WhatsAppConnectionWizardExternalLink, {
  WhatsAppConnectionWizardAdvisorLink,
  WhatsAppConnectionWizardAdvisorRow,
} from './WhatsAppConnectionWizardExternalLink.component';

/**
 * The WhatsApp API connection wizard: a split modal whose first step asks
 * where the team will answer its conversations, which picks Coexistencia or
 * Cloud API, and opens Meta on that path, then shows
 * the connection state screens in its main pane.
 *
 * ModalsProvider mounts it in place of `WhatsAppBusinessConnectionModal` when
 * `integrations_wapi-connection-wizard` is loaded and on, and only while the
 * modal is open or leaving. Its connection state lives in
 * `WhatsAppConnectionWizardProvider`, which stays mounted while it is closed.
 */
export default function WhatsAppConnectionWizard() {
  const wizard = useWhatsAppConnectionWizard();

  // The provider publishes its state right after the first open mounts it,
  // before the browser paints, so this empty render is never seen.
  if (!wizard) return null;
  return <WhatsAppConnectionWizardContent {...wizard} />;
}

function WhatsAppConnectionWizardContent(props: tWhatsAppConnectionWizardHook) {
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const { active: isSyncActive, syncErrorCode } = useProfilingProgressContext();
  const { trackWapiEvent } = useWapiAnalytics();
  const { whatsAppBusinessConnectionModalSource } = useModalsContext();
  const {
    isOpen,
    state,
    moment: connectionMoment,
    selectedPath,
    selectPath,
    handleClose,
    handleConnect,
    handleRetry,
    handleEscalatedMount,
    handleAdvisorCtaClick,
    backToConnect,
    handleConnectWithoutGuide,
    isMetaInAnotherTab = false,
    restoredFailureAt,
    activationFailureCount = 0,
    exchangeErrorCount = 0,
    phoneNumber,
    activationDecidedAt,
    simulatedActivatingStage = 'start',
    playsBrandMoment,
    simulatedHistorySync,
  } = props;
  const titleId = useId();
  const questionId = useId();
  const asideTitleId = useId();
  const pathGroupName = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const firstAnswerRef = useRef<HTMLInputElement>(null);

  // Set by a press of "Continuar en Meta" with no answer. Shown only while
  // no answer is chosen, and cleared when the modal closes, so a reopened
  // step 1 asks again only after another such press.
  const [isAnswerRequested, setIsAnswerRequested] = useState(false);
  const isAnswerMissingShown = isAnswerRequested && selectedPath === null;
  useEffect(() => {
    if (!isOpen) setIsAnswerRequested(false);
  }, [isOpen]);

  // Closing clears the opener's source, so the one read while open is kept
  // and the footer does not change while the modal fades out.
  const openerSourceRef = useRef(whatsAppBusinessConnectionModalSource);
  if (isOpen) openerSourceRef.current = whatsAppBusinessConnectionModalSource;
  const isOpenedFromOnboarding = openerSourceRef.current === 'onboarding';

  // Read during the first render, before the focus trap moves focus inside,
  // so it is the control that opened the modal.
  const [openerElement] = useState(() => document.activeElement);
  const restoreOpenerFocus = useCallback(() => {
    if (openerElement instanceof HTMLElement && openerElement.isConnected) {
      openerElement.focus();
    }
  }, [openerElement]);

  useFocusTrap({ containerRef, onEscape: handleClose });

  // The modal stays mounted while it fades out, so focus goes back as soon as
  // it closes, and again on unmount for a modal removed while open.
  useEffect(() => {
    if (!isOpen) restoreOpenerFocus();
  }, [isOpen, restoreOpenerFocus]);
  useEffect(() => restoreOpenerFocus, [restoreOpenerFocus]);

  // The focus trap handles Escape from inside the content; this covers a key
  // pressed with focus on the dialog element itself.
  function handleDialogKeyDown(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key === 'Escape') handleClose();
  }

  // `skipped` can be restored from onboarding's storage; like `connect`, it
  // still has to choose a path.
  const isChoosingPath = connectionMoment.kind === 'choose_path';
  const isActivationMoment =
    connectionMoment.kind === 'activating' ||
    connectionMoment.kind === 'verifying' ||
    connectionMoment.kind === 'activation_failed';
  const errorAsideMoment =
    connectionMoment.kind === 'meta_error_window_open' ||
    connectionMoment.kind === 'meta_error_window_closed' ||
    connectionMoment.kind === 'closed_before_finishing'
      ? connectionMoment
      : null;
  const isMetaWindowOpen = connectionMoment.kind === 'meta_window_open';
  const isConnected = connectionMoment.kind === 'connected';
  // Only a launch in this session records the path; a screen restored from
  // the integration row does not know it. A simulated scene shows the path it
  // was opened on.
  const connectionPath =
    (state.isSimulated ? selectedPath : state.path) ?? undefined;
  const activationFailure =
    connectionMoment.kind === 'activation_failed'
      ? resolveActivationFailure({
          reason: state.activationReason,
          failureCount: activationFailureCount,
        })
      : null;
  // An error with a known code keeps the screen of its cause, actions
  // included, so it resolves to null here.
  const exchangeError =
    connectionMoment.kind === 'exchange_error'
      ? resolveExchangeError({
          errorCode: state.errorCode,
          errorCount: exchangeErrorCount,
        })
      : null;
  const failureFooter =
    activationFailure?.footer ??
    exchangeError?.footer ??
    resolveFailureFooter(connectionMoment);
  const showsConnectWithoutGuide = canConnectWithoutGuide(
    connectionMoment,
    failureFooter,
  );
  // Once Meta handed over a code, the aside sums up the connection it is for.
  const showsConnectionSummary =
    isActivationMoment || isConnected || exchangeError !== null;

  // Read at render, plus one timer to the crossing, so a person who keeps
  // the pending screen open sees the advisor take the lead at the hour.
  const isPending = connectionMoment.kind === 'verifying';
  const millisecondsUntilEscalation = isPending
    ? millisecondsUntilActivationEscalation({
        activationDecidedAt,
        now: Date.now(),
      })
    : null;
  const isPendingPastEscalation =
    millisecondsUntilEscalation !== null && millisecondsUntilEscalation <= 0;
  const [, setEscalationCheckCount] = useState(0);
  useEffect(() => {
    if (!isPending) return undefined;
    const delay = millisecondsUntilActivationEscalation({
      activationDecidedAt,
      now: Date.now(),
    });
    if (delay === null || delay <= 0) return undefined;
    const timer = setTimeout(
      () => setEscalationCheckCount((count) => count + 1),
      delay,
    );
    return () => clearTimeout(timer);
  }, [isPending, activationDecidedAt]);
  // On a phone a stacked three-button footer is too tall, so "Cambiar de
  // camino" and "Conectar sin la guía" move to the end of the main pane. One
  // copy at a time, so the accessibility tree never holds a hidden twin.
  const isDesktopViewport = useMediaQuery(WIZARD_DESKTOP_MEDIA_QUERY);
  // Until an answer is chosen the panel lists only what both paths need.
  const afterConnectingRows = selectedPath
    ? WIZARD_AFTER_CONNECTING_ROWS[selectedPath]
    : [];
  const haveAtHandRows: tWizardAsideRow[] = [
    ...WIZARD_REQUIREMENT_ROWS,
    ...(selectedPath
      ? WIZARD_PATH_REQUIREMENT_ROWS[selectedPath]
      : [WIZARD_GENERAL_PHONE_REQUIREMENT_ROW]),
  ];
  // While Meta's code is exchanged the phase is still `connecting`, but Meta's
  // part is done and the screen is the activation one.
  const currentStep =
    connectionMoment.kind === 'activating'
      ? WIZARD_STEP_COUNT
      : WIZARD_STEP_BY_PHASE[state.phase];
  // A failed activation or exchange is still the bar's current step, but its
  // segment stays empty: a full bar would read as a finished connection.
  const filledSegmentCount =
    connectionMoment.kind === 'activation_failed' ||
    connectionMoment.kind === 'exchange_error'
      ? currentStep - 1
      : currentStep;
  let asideTitleKey = 'wizard.aside.title';
  if (isMetaWindowOpen) asideTitleKey = 'wizard.metaGuide.title';
  else if (showsConnectionSummary) {
    asideTitleKey = 'wizard.asideConnection.title';
  }

  // Each screen reports itself as the location of its advisor links.
  function reportAdvisorClickAt(location: tWizardAdvisorLocation) {
    return (kind: tConnectionFailedAdvisorCtaKind) =>
      handleAdvisorCtaClick({ kind, location });
  }

  // The footer leads with the advisor on a repeated failure, an activation
  // failure with no known reason, or a catalog error only an advisor solves.
  function resolveFooterAdvisorLocation(): tWizardAdvisorLocation {
    if (activationFailure) {
      return activationFailure.isRepeat
        ? 'activation_failed_repeat'
        : 'activation_failed';
    }
    if (exchangeError) {
      return exchangeError.isRepeat
        ? 'exchange_error_repeat'
        : 'exchange_error';
    }
    if (connectionMoment.kind === 'closed_before_finishing') {
      return 'meta_window_closed_repeat';
    }
    return 'error_catalog';
  }

  // A simulated screen is a preview, so its clicks are not product usage. An
  // attempt restored without a path sends no `path` key.
  function handleConnectionVideoClick(
    location: tWizardConnectionVideoLocation,
  ) {
    if (state.isSimulated) return;
    trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_VIDEO_CLICKED, {
      ...(state.path ? { path: state.path } : {}),
      location,
      connection_flow: WIZARD_CONNECTION_FLOW,
    });
  }

  function renderStepProgress() {
    const steps = Array.from(
      { length: WIZARD_STEP_COUNT },
      (_, index) => index + 1,
    );
    return (
      <div
        role="progressbar"
        aria-label={t('wizard.progress.label')}
        aria-valuemin={1}
        aria-valuemax={WIZARD_STEP_COUNT}
        aria-valuenow={currentStep}
        aria-valuetext={t(`wizard.progress.step${currentStep}`)}
        className="flex gap-[var(--ls-spacing-3)]"
      >
        {steps.map((step) => (
          <span
            key={step}
            aria-hidden="true"
            className={classNames(
              'h-1.5 flex-1 rounded-[var(--ls-radius-full)] transition-colors',
              step <= filledSegmentCount
                ? 'bg-[var(--ls-accent-primary-default)]'
                : 'bg-[var(--ls-border-neutral-lighter)]',
            )}
          />
        ))}
      </div>
    );
  }

  function renderAnswerPoint(labelKey: string, point: tWizardPathAnswerPoint) {
    return (
      <span className="flex flex-col gap-[var(--ls-spacing-1)]">
        <span className="flex items-center gap-[var(--ls-spacing-2)]">
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--ls-radius-md)] bg-[var(--ls-accent-primary-lighter)] text-[var(--ls-accent-primary-default)]"
          >
            <point.icon className="h-3.5 w-3.5" />
          </span>
          <span className="text-[length:var(--ls-body)] font-medium text-[var(--ls-text-neutral-default)]">
            {t(labelKey)}
          </span>
        </span>
        <span className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
          {t(point.textKey)}
        </span>
      </span>
    );
  }

  function renderPathCard(card: tWizardPathCard, index: number) {
    const isSelected = card.path === selectedPath;
    const answerId = `${pathGroupName}-${card.path}-answer`;
    const pointsId = `${pathGroupName}-${card.path}-points`;

    return (
      <label
        key={card.path}
        className={classNames(
          'flex cursor-pointer flex-col gap-[var(--ls-spacing-3)] rounded-[var(--ls-radius-lg)] border p-[var(--ls-spacing-4)] transition-colors md:gap-[var(--ls-spacing-2)] md:p-[var(--ls-spacing-3)]',
          isSelected
            ? 'border-[var(--ls-accent-primary-default)] ring-1 ring-[var(--ls-accent-primary-default)]'
            : 'border-[var(--ls-border-neutral-light)]',
        )}
      >
        <span className="flex items-center justify-between gap-[var(--ls-spacing-3)]">
          <span className="flex items-center gap-[var(--ls-spacing-2)]">
            <input
              ref={index === 0 ? firstAnswerRef : undefined}
              type="radio"
              name={pathGroupName}
              value={card.path}
              checked={isSelected}
              onChange={() => selectPath(card.path)}
              aria-labelledby={answerId}
              aria-describedby={pointsId}
              className="h-4 w-4 shrink-0 accent-[var(--ls-accent-primary-default)]"
            />
            <span
              id={answerId}
              className="text-[length:var(--ls-body-lg)] font-bold text-[var(--ls-text-neutral-default)]"
            >
              {t(card.answerKey)}
            </span>
          </span>
          <WhatsAppConnectionWizardPathDrawing keepsApp={card.keepsApp} />
        </span>
        <span
          id={pointsId}
          className="grid grid-cols-1 gap-[var(--ls-spacing-3)] rounded-[var(--ls-radius-md)] bg-[var(--ls-surface-background)] p-[var(--ls-spacing-3)] sm:grid-cols-2"
        >
          {renderAnswerPoint('wizard.paths.gainLabel', card.gain)}
          {renderAnswerPoint('wizard.paths.watchLabel', card.watch)}
        </span>
      </label>
    );
  }

  function renderAsideGroup(group: tWizardAsideGroup) {
    return (
      <div
        data-testid={group.testId}
        className={classNames(
          'flex-col gap-[var(--ls-spacing-3)]',
          group.className,
        )}
      >
        <p className="text-[length:var(--ls-caption)] font-medium text-[var(--ls-text-neutral-default)]">
          {t(group.labelKey)}
        </p>
        <ul className="flex flex-col gap-[var(--ls-spacing-4)]">
          {group.rows.map((row) => (
            <li
              key={row.textKey}
              className="flex gap-[var(--ls-spacing-3)] text-[length:var(--ls-body)] text-[var(--ls-text-neutral-default)]"
            >
              <row.icon className={WIZARD_ICON_CLASS_NAME} aria-hidden="true" />
              {/* Trans so a row can set its lead-in apart, like the history
                  row; medium weight because a bare strong renders bolder. */}
              <span>
                <Trans
                  t={t}
                  i18nKey={row.textKey}
                  components={{ strong: <strong className="font-medium" /> }}
                />
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderPathChoice() {
    return (
      <>
        <div className="flex flex-col gap-[var(--ls-spacing-3)]">
          <div
            role="radiogroup"
            aria-labelledby={questionId}
            className="grid auto-rows-fr gap-[var(--ls-spacing-3)]"
          >
            {WIZARD_PATH_CARDS.map(renderPathCard)}
          </div>
          {/* The answer, if any, stays as it is. */}
          <WhatsAppConnectionWizardAdvisorRow
            onAdvisorCtaClick={reportAdvisorClickAt('choose_path')}
          />
        </div>
        {/* Read at render: a window that fills the screen opens Meta as
            another tab, which the person has to come back from. */}
        {isWindowFullScreen() && (
          <p className={WIZARD_BODY_CLASS_NAME}>
            {t('wizard.fullscreenNotice')}
          </p>
        )}
        {/* Below md the aside comes after the answers, so "Después de
            conectar" moves up here to be read before opening Meta. */}
        {afterConnectingRows.length > 0 &&
          renderAsideGroup({
            testId: 'wizard-after-connecting-below-cards',
            labelKey: 'wizard.aside.groups.afterConnecting',
            rows: afterConnectingRows,
            className: 'flex md:hidden',
          })}
      </>
    );
  }

  // A failure restored after a reload can be hours old, so it says when it
  // happened instead of reading as if it just did. Moment's global locale
  // follows the app language (src/i18n.ts), so the elapsed time matches it.
  function renderRestoredFailureLine() {
    if (restoredFailureAt === undefined) return null;
    const elapsed = moment(restoredFailureAt).fromNow(true);
    return (
      <p className="text-center text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
        {t('restoredFailure.lastAttempt', { timeAgo: elapsed })}
      </p>
    );
  }

  function renderStateScreen() {
    // Meta's window decides these screens, which the phase alone cannot tell
    // apart.
    switch (connectionMoment.kind) {
      case 'meta_window_open':
        return (
          <WhatsAppConnectionWizardWaiting
            isMetaInAnotherTab={isMetaInAnotherTab}
            path={connectionPath}
          />
        );

      case 'meta_error_window_open':
      case 'meta_error_window_closed':
        return (
          <>
            {renderRestoredFailureLine()}
            <WhatsAppConnectionWizardReportedError
              errorReport={connectionMoment.errorReport}
              isMetaWindowOpen={
                connectionMoment.kind === 'meta_error_window_open'
              }
              onAdvisorCtaClick={reportAdvisorClickAt(
                findWizardErrorCatalogEntry(connectionMoment.errorReport.code)
                  ? 'error_catalog'
                  : 'error_reported',
              )}
            />
          </>
        );

      case 'closed_before_finishing':
        return (
          <>
            {renderRestoredFailureLine()}
            <WhatsAppConnectionWizardClosedBeforeFinishing
              step={connectionMoment.step}
              isRepeat={connectionMoment.isRepeat}
              onAdvisorCtaClick={reportAdvisorClickAt('meta_window_closed')}
              onConnectionVideoClick={handleConnectionVideoClick}
            />
          </>
        );

      // Keyed by where it starts, so a simulated scene that starts earlier
      // runs its delays again.
      case 'activating':
        return (
          <WhatsAppConnectionWizardActivating
            key={simulatedActivatingStage}
            startStage={simulatedActivatingStage}
          />
        );

      // A simulated screen shows its sample sync, never the workspace's.
      case 'connected':
        return (
          <WhatsAppConnectionWizardConnected
            path={connectionPath}
            historySync={
              simulatedHistorySync ??
              resolveHistorySync({
                isActive: isSyncActive,
                errorCode: syncErrorCode,
              })
            }
            playsBrandMoment={playsBrandMoment}
            onAdvisorCtaClick={reportAdvisorClickAt(
              'connected_without_history',
            )}
          />
        );
    }

    switch (state.phase) {
      case 'failed_error':
        return (
          <>
            {renderRestoredFailureLine()}
            {exchangeError ? (
              <WhatsAppConnectionWizardExchangeError
                isRepeat={exchangeError.isRepeat}
                onAdvisorCtaClick={reportAdvisorClickAt('exchange_error')}
              />
            ) : (
              <ConnectionFailedError
                onRetry={handleRetry}
                onBack={backToConnect}
                errorCode={state.errorCode}
                translationNamespace={WIZARD_TRANSLATION_NAMESPACE}
                advisorRow={
                  <WhatsAppConnectionWizardAdvisorRow
                    onAdvisorCtaClick={reportAdvisorClickAt(
                      'connection_failed',
                    )}
                  />
                }
              />
            )}
          </>
        );

      case 'verifying':
        return (
          <WhatsAppConnectionWizardActivationPending
            isPastEscalation={isPendingPastEscalation}
          />
        );

      case 'activation_failed':
        return activationFailure ? (
          <WhatsAppConnectionWizardActivationFailed
            activationFailure={activationFailure}
            onAdvisorCtaClick={reportAdvisorClickAt('activation_failed')}
          />
        ) : null;

      case 'escalated':
        return (
          <ConnectionEscalated
            onBack={backToConnect}
            signupStep={state.signupStep}
            signupErrorReport={state.signupErrorReport}
            translationNamespace={WIZARD_TRANSLATION_NAMESPACE}
            onMount={handleEscalatedMount}
            onAdvisorCtaClick={reportAdvisorClickAt('escalated')}
          />
        );

      default:
        return null;
    }
  }

  function renderChangePathButton(className: string) {
    return (
      <Button
        color="ghost"
        size="md"
        type="button"
        className={className}
        onClick={backToConnect}
      >
        {t('wizard.actions.changePath')}
      </Button>
    );
  }

  // An action inside the app, so a button: it weighs less than «Ahora no».
  function renderConnectWithoutGuideButton(className?: string) {
    return (
      <Button
        color="ghost"
        size="sm"
        type="button"
        className={className}
        onClick={handleConnectWithoutGuide}
      >
        {t('wizard.actions.connectWithoutGuide')}
      </Button>
    );
  }

  function renderBackToHomeButton(color: 'primary' | 'tertiary' | 'ghost') {
    return (
      <Button
        color={color}
        size="md"
        type="button"
        className="w-full md:w-auto"
        onClick={handleClose}
      >
        {t('wizard.actions.backToHome')}
      </Button>
    );
  }

  function renderFooter() {
    // The funnel is where the connection pays off; leaving is the way back.
    // Onboarding has no home to go back to: any exit from it leads on to the
    // funnel.
    if (isConnected) {
      if (isOpenedFromOnboarding) {
        return <WhatsAppConnectionWizardGoToFunnelButton />;
      }
      return (
        <>
          {renderBackToHomeButton('ghost')}
          <WhatsAppConnectionWizardGoToFunnelButton />
        </>
      );
    }

    // Leaving costs nothing while the number activates: it keeps activating.
    if (connectionMoment.kind === 'activating') {
      return renderBackToHomeButton('tertiary');
    }

    // Past the escalation time the advisor is what can move the connection.
    if (connectionMoment.kind === 'verifying') {
      if (!isPendingPastEscalation) return renderBackToHomeButton('primary');
      return (
        <>
          {renderBackToHomeButton('ghost')}
          <WhatsAppConnectionWizardAdvisorLink
            variant="primary"
            onAdvisorCtaClick={reportAdvisorClickAt('verifying_long')}
            className="w-full md:w-auto"
          />
        </>
      );
    }

    if (failureFooter) {
      const isRetryPrimary = failureFooter.primary === 'retry';
      return (
        <>
          {/* Both side exits sit at the start, before the actions that lead. */}
          {isDesktopViewport &&
            renderChangePathButton(showsConnectWithoutGuide ? '' : 'mr-auto')}
          {isDesktopViewport &&
            showsConnectWithoutGuide &&
            renderConnectWithoutGuideButton('mr-auto')}
          {/* Retrying leads only when it can work now; otherwise its label
              names what has to happen first. */}
          <Button
            color={isRetryPrimary ? 'primary' : 'ghost'}
            size="md"
            type="button"
            className="w-full md:w-auto"
            onClick={handleRetry}
          >
            {t(failureFooter.retryLabelKey)}
          </Button>
          {failureFooter.primary === 'advisor' && (
            <WhatsAppConnectionWizardAdvisorLink
              variant="primary"
              onAdvisorCtaClick={reportAdvisorClickAt(
                resolveFooterAdvisorLocation(),
              )}
              className="w-full md:w-auto"
            />
          )}
          {failureFooter.primary === 'openInMeta' &&
            failureFooter.metaLinkLabelKey && (
              <WhatsAppConnectionWizardExternalLink
                href={failureFooter.metaUrl}
                label={t(failureFooter.metaLinkLabelKey)}
                variant="primary"
                className="w-full md:w-auto"
              />
            )}
        </>
      );
    }

    // The other state screens carry their own calls to action, and Meta's
    // open window leaves nothing to do here until it closes.
    if (!isChoosingPath) return null;

    // Closing is the modal's X; the footer only moves on.
    const isConnecting = state.phase === 'connecting';
    return (
      <>
        {isDesktopViewport && renderConnectWithoutGuideButton('mr-auto')}
        <Button
          color="primary"
          size="md"
          type="button"
          className="w-full md:w-auto"
          onClick={handleConnectClick}
          disabled={isConnecting}
        >
          {isConnecting
            ? t('wizard.footer.connecting')
            : t('wizard.footer.connect')}
        </Button>
      </>
    );
  }

  // The button stays enabled with no answer: the press asks for one under
  // the question and leads to the first answer, and Meta stays closed.
  function handleConnectClick() {
    if (selectedPath === null) {
      setIsAnswerRequested(true);
      firstAnswerRef.current?.focus();
      return;
    }
    handleConnect();
  }

  // Shown on every screen before the connection, Meta's window included, so
  // the person keeps the chosen path's consequences in view while connecting.
  // While the number activates, nothing in "after connecting" applies yet.
  function renderPathAsideGroups() {
    return (
      <>
        {afterConnectingRows.length > 0 &&
          connectionMoment.kind !== 'activating' &&
          renderAsideGroup({
            testId: 'wizard-aside-keep-in-mind',
            labelKey: 'wizard.aside.groups.afterConnecting',
            rows: afterConnectingRows,
            // Below md step 1 shows this group under the answers instead.
            className: isChoosingPath ? 'hidden md:flex' : 'flex',
          })}
        {renderAsideGroup({
          testId: 'wizard-aside-have-at-hand',
          labelKey: 'wizard.aside.groups.haveAtHand',
          rows: haveAtHandRows,
          className: 'flex',
        })}
      </>
    );
  }

  const footerContent = renderFooter();

  return (
    <Modal
      open={isOpen}
      responsive={false}
      role="dialog"
      aria-modal="true"
      // Named by the step 1 title, which the later screens keep for assistive
      // technology only, since they carry their own headings.
      aria-labelledby={titleId}
      onKeyDown={handleDialogKeyDown}
      className="WhatsAppConnectionWizard flex w-full max-w-5xl flex-col overflow-hidden p-0 md:h-[min(53.25rem,calc(100vh-5em))]"
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        className="flex min-h-0 flex-1 flex-col outline-none"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          <section
            className={classNames(
              'flex flex-col gap-[var(--ls-spacing-6)] bg-[var(--ls-surface-elevation-01)] p-[var(--ls-spacing-6)] md:basis-3/5 md:overflow-y-auto',
              // Tighter on step 1 so the question, both answers and the
              // advisor row fit a 768 px tall screen without scrolling.
              isChoosingPath
                ? 'md:gap-[var(--ls-spacing-4)] md:px-[var(--ls-spacing-10)] md:py-[var(--ls-spacing-8)]'
                : 'md:p-[var(--ls-spacing-10)]',
            )}
          >
            {renderStepProgress()}
            {isChoosingPath ? (
              <header className="flex flex-col gap-[var(--ls-spacing-2)]">
                <h2
                  id={titleId}
                  className="text-[length:var(--ls-h5)] font-bold text-[var(--ls-text-neutral-default)]"
                >
                  {t('wizard.title')}
                </h2>
                <p
                  id={questionId}
                  className="text-[length:var(--ls-body-lg)] text-[var(--ls-text-neutral-default)]"
                >
                  {t('wizard.question')}
                </p>
                {isAnswerMissingShown && (
                  <p
                    role="alert"
                    className="text-[length:var(--ls-body)] text-[var(--ls-text-error)]"
                  >
                    {t('wizard.answerRequired')}
                  </p>
                )}
              </header>
            ) : (
              <span id={titleId} className="sr-only">
                {t('wizard.title')}
              </span>
            )}
            {isChoosingPath ? renderPathChoice() : renderStateScreen()}
            {failureFooter &&
              !isDesktopViewport &&
              renderChangePathButton('w-full')}
            {showsConnectWithoutGuide &&
              !isDesktopViewport &&
              renderConnectWithoutGuideButton('w-full')}
          </section>
          <aside
            // The error aside's sections carry their own headings.
            aria-labelledby={errorAsideMoment ? undefined : asideTitleId}
            className="relative flex flex-col gap-[var(--ls-spacing-4)] bg-[var(--ls-surface-background)] p-[var(--ls-spacing-6)] md:basis-2/5 md:overflow-y-auto md:p-[var(--ls-spacing-10)]"
          >
            <div
              className={classNames(
                'flex items-start gap-[var(--ls-spacing-2)]',
                errorAsideMoment ? 'justify-end' : 'justify-between',
              )}
            >
              {!errorAsideMoment && (
                <h3
                  id={asideTitleId}
                  className="text-[length:var(--ls-subtitle)] font-bold text-[var(--ls-text-neutral-default)]"
                >
                  {t(asideTitleKey)}
                </h3>
              )}
              <Button
                color="ghost"
                size="sm"
                type="button"
                iconOnly
                aria-label={t('wizard.close')}
                onClick={handleClose}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
            {errorAsideMoment ? (
              // Keyed by the moment, so a new report or close opens on its
              // default view.
              <WhatsAppConnectionWizardErrorAside
                key={errorAsideMoment.kind}
                moment={errorAsideMoment}
                selectedPath={selectedPath}
                onAdvisorCtaClick={reportAdvisorClickAt('error_catalog')}
              />
            ) : isMetaWindowOpen ? (
              <div className="flex flex-col gap-[var(--ls-spacing-6)]">
                {selectedPath && (
                  <WhatsAppConnectionWizardMetaGuide path={selectedPath} />
                )}
                {renderPathAsideGroups()}
              </div>
            ) : (
              <div className="flex flex-col gap-[var(--ls-spacing-6)]">
                {showsConnectionSummary && (
                  <WhatsAppConnectionWizardConnectionSummary
                    selectedPath={connectionPath}
                    phoneNumber={phoneNumber}
                  />
                )}
                {isConnected && <WhatsAppConnectionWizardTeamInvite />}
                {!isConnected && renderPathAsideGroups()}
              </div>
            )}
          </aside>
        </div>
        {footerContent && (
          <footer className="flex flex-col gap-[var(--ls-spacing-3)] border-t border-[var(--ls-border-neutral-light)] bg-[var(--ls-surface-elevation-01)] px-[var(--ls-spacing-6)] py-[var(--ls-spacing-4)] md:flex-row md:justify-end md:px-[var(--ls-spacing-10)]">
            {footerContent}
          </footer>
        )}
      </div>
    </Modal>
  );
}
